import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';

import { cx } from './ui';

interface ToastMessage {
  id: number;
  text: string;
  tone: 'success' | 'error';
}

interface ToastApi {
  success: (text: string) => void;
  failure: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

// Long enough to read a sentence, short enough not to sit over the table.
const DISMISS_AFTER_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const push = useCallback((text: string, tone: ToastMessage['tone']) => {
    const id = Date.now() + Math.random();
    setMessages((current) => [...current, { id, text, tone }]);
    setTimeout(
      () => setMessages((current) => current.filter((message) => message.id !== id)),
      DISMISS_AFTER_MS,
    );
  }, []);

  const value = useMemo<ToastApi>(
    () => ({
      success: (text) => push(text, 'success'),
      failure: (text) => push(text, 'error'),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        `polite` and never focused: a confirmation must be announced without
        yanking the cursor out of whatever the user is doing next.
      */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 left-1/2 z-[60] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={cx(
              'pointer-events-auto flex items-start gap-2 rounded-lg px-3 py-2.5 shadow-overlay',
              'text-sm',
              message.tone === 'success'
                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                : 'bg-danger text-white',
            )}
          >
            {message.tone === 'success' ? (
              <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
